import { AsyncPipe, CommonModule, NgForOf, NgIf } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { getHttpErrorMessage } from "../../../shared/utils/http-error.util";
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { RouterLink } from "@angular/router";
import { TuiDay, TuiValidationError } from "@taiga-ui/cdk";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiError,
  TuiLink,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBreadcrumbs,
  TuiDataListWrapper,
  TuiFade,
  TuiFieldErrorPipe,
  TuiFiles,
  TuiFilterByInputPipe,
  TuiStringifyContentPipe,
  TuiTabs,
} from "@taiga-ui/kit";
import { TuiCell, TuiNavigation } from "@taiga-ui/layout";
import {
  TuiComboBoxModule,
  TuiInputDateModule,
  TuiInputModule,
  TuiInputPhoneModule,
  TuiMultiSelectModule,
  TuiSelectModule,
  TuiTextareaModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { catchError, of, switchMap } from "rxjs";
import { ClientService } from "../../../core/services/client.service";
import { TuiTable } from "@taiga-ui/addon-table";
import { InvoiceService } from "../../../core/services/invoice.service";
import { NurseService } from "../../../core/services/nurse.service";
import { SalaryService } from "../../../core/services/salary.service";
import { ShiftService } from "../../../core/services/shift.service";
import { ChargesService } from "../../../core/services/charges.service";
import { formatClientIdentifier } from "../../../shared/utils/client-identifier";
import { EnterSubmitDirective } from "../../../core/directives/enter-submit.directive";

@Component({
  selector: "app-nurse-checkin",
  imports: [
    EnterSubmitDirective,
    FormsModule,
    CommonModule,
    TuiAppearance,
    TuiButton,
    TuiDataList,
    TuiDropdown,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiFiles,
    TuiFieldErrorPipe,
    TuiError,
    ReactiveFormsModule,
    AsyncPipe,
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiTextareaModule,
    TuiInputPhoneModule,
    TuiComboBoxModule,
    TuiMultiSelectModule,
    TuiSelectModule,
    TuiDataListWrapper,
    RouterLink,
    TuiInputDateModule,
    TuiTable,
    TuiStringifyContentPipe,
    TuiFilterByInputPipe,
  ],
  templateUrl: "./nurse-checkin.component.html",
  styleUrl: "./nurse-checkin.component.scss",
})
export class NurseCheckinComponent implements OnInit {
  clients: any[] = [];
  nurseId: string = "";
  shifts: any[] = [];
  checkInForm: FormGroup;
  isLoading = false;
  nurse: any;

  protected readonly rosterItems = [
    { items: "12 hours shift", value: 12 },
    { items: "24 hours shift", value: 24 },
  ];

  protected readonly mealOptions = [
    { items: "With Meal" },
    { items: "Without Meal" },
  ];

  protected readonly typeOptions = [
    { items: "Normal" },
    { items: "Baby Care" },
    { items: "NHP (National Hospital)" },
  ];

  constructor(
    private clientService: ClientService,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private alerts: TuiAlertService,
    private nurseService: NurseService,
    private salaryService: SalaryService,
    private shiftService: ShiftService,
    private chargesService: ChargesService
  ) {
    this.checkInForm = this.fb.group({
      client: [""],
      address: [{ value: "", disabled: true }, Validators.required],
      mealOption: ["", Validators.required],
      typeOption: ["", Validators.required],
      roster: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    this.nurseId = this.route.snapshot.paramMap.get("id")!;
    this.loadClients();
    this.loadNurseData();

    this.checkInForm.get("typeOption")?.valueChanges.subscribe((value: any) => {
      if (value && value.items === "NHP (National Hospital)") {
        this.checkInForm.get("address")?.setValue("NHP (National Hospital)");
        this.checkInForm.get("address")?.disable();
      } else if (
        (value && value.items === "Normal") ||
        value.items === "Baby Care"
      ) {
        this.checkInForm.get("address")?.enable();
        this.checkInForm.get("address")?.setValue("");
      }
    });

    this.checkInForm.get("roster")?.valueChanges.subscribe((value: any) => {
      if (value && value.items === "24 hours shift") {
        const withMealOption = this.mealOptions.find(
          (opt) => opt.items === "With Meal"
        );
        this.checkInForm.get("mealOption")?.setValue(withMealOption);
        this.checkInForm.get("mealOption")?.disable();
      } else if (value && value.items === "12 hours shift") {
        this.checkInForm.get("mealOption")?.enable();
        this.checkInForm.get("mealOption")?.setValue(null);
      }
    });
  }

  loadNurseData(): void {
    this.isLoading = true;
    if (this.nurseId) {
      this.nurseService.getOneNurseById(this.nurseId).subscribe((res: any) => {
        console.log(res);
        this.nurse = res;
        this.isLoading = false;
      });
    }
  }

  loadClients(): void {
    this.clientService.getAllClients().subscribe({
      next: (response: any) => {
        const clients = Array.isArray(response) ? response : [];
        this.clients = clients.filter((c: any) => !c?.archived && c?.active !== false);
      },
      error: (error) => {
        console.error("Error fetching clients:", error);
      },
    });
  }

  stringifyClients(client: any): string {
    if (!client) return "";
    if (typeof client === "object") {
      return `${formatClientIdentifier(client)} - ${client.name}`;
    }
    const foundClient = this.clients.find((c: any) => c._id === client);
    return foundClient
      ? `${formatClientIdentifier(foundClient)} - ${foundClient.name}`
      : "";
  }

  stringifyDropdown(item: { items: string; value: number } | null): string {
    if (!item) return "";
    return item.items;
  }

  checkIn(): void {
    if (this.checkInForm.invalid) {
      this.checkInForm.markAllAsTouched();
      return;
    }

    const currentTime = new Date();
    const rosterHours = this.checkInForm.get("roster")?.value?.value;
    const endTime = new Date(
      currentTime.getTime() + rosterHours * 60 * 60 * 1000
    );

    // ----------------------------
    // 1) CALCULATE NURSE SALARY
    // ----------------------------
    const previousSalary = this.nurse.salary || 0;
    let shiftSalary = 0;

    const experience = this.nurse.experienceLevel;
    const roster = this.checkInForm.get("roster")?.value;
    const typeOption = this.checkInForm.get("typeOption")?.value;
    const mealOption = this.checkInForm.get("mealOption")?.value;

    if (experience === "Six Months") {
      if (roster.value === 12) {
        // 12-hour shift
        if (typeOption.items === "Normal") {
          shiftSalary = mealOption.items === "With Meal" ? 2000 : 2500;
        } else if (typeOption.items === "Baby Care") {
          shiftSalary = mealOption.items === "With Meal" ? 2500 : 3000;
        } else if (typeOption.items === "NHP (National Hospital)") {
          shiftSalary = mealOption.items === "With Meal" ? 2500 : 3000;
        }
      } else if (roster.value === 24) {
        // 24-hour shift (no "Without Meal" option)
        if (typeOption.items === "Normal") {
          shiftSalary = 2000;
        } else if (typeOption.items === "Baby Care") {
          shiftSalary = 3000;
        } else if (typeOption.items === "NHP (National Hospital)") {
          shiftSalary = 3000;
        }
      }
    } else {
      // More than one year
      if (roster.value === 12) {
        if (typeOption.items === "Normal") {
          shiftSalary = mealOption.items === "With Meal" ? 2050 : 2500;
        } else if (typeOption.items === "Baby Care") {
          shiftSalary = mealOption.items === "With Meal" ? 2500 : 3000;
        } else if (typeOption.items === "NHP (National Hospital)") {
          shiftSalary = mealOption.items === "With Meal" ? 2500 : 3000;
        }
      } else if (roster.value === 24) {
        if (typeOption.items === "Normal") {
          shiftSalary = 2220;
        } else if (typeOption.items === "Baby Care") {
          shiftSalary = 3000;
        } else if (typeOption.items === "NHP (National Hospital)") {
          shiftSalary = 3000;
        }
      }
    }

    const newSalary = previousSalary + shiftSalary;

    // ----------------------------
    // 2) CALCULATE CLIENT CHARGES
    // ----------------------------
    const selectedClient = this.clients.find(
      (client) => client._id === this.checkInForm.get("client")?.value._id
    );
    const previousCharges = selectedClient ? selectedClient.charges || 0 : 0;
    let shiftCharges = 0;

    // 12-hour shift logic
    if (roster?.value === 12) {
      if (typeOption?.items === "Normal") {
        shiftCharges = mealOption?.items === "With Meal" ? 3000 : 4000;
      } else if (typeOption?.items === "Baby Care") {
        shiftCharges = mealOption?.items === "With Meal" ? 4000 : 5000;
      } else if (typeOption?.items === "NHP (National Hospital)") {
        shiftCharges = mealOption?.items === "With Meal" ? 4000 : 5000;
      }
    }
    // 24-hour shift logic
    else if (roster?.value === 24) {
      // 24-hour shift does not have "Without Meal"
      if (typeOption?.items === "Normal") {
        shiftCharges = 4000;
      } else if (typeOption?.items === "Baby Care") {
        shiftCharges = 5000;
      } else if (typeOption?.items === "NHP (National Hospital)") {
        shiftCharges = 5000;
      }
    }

    const newCharges = previousCharges + shiftCharges;

    
    // ----------------------------
    // 3) CREATE SHIFT OBJECT
    // ----------------------------
    const newShift: any = {
      nurseID: this.nurse._id,
      clientID: this.checkInForm.get("client")?.value._id,
      date: currentTime,
      startTime: currentTime,
      endTime: endTime,
      rooster: rosterHours,
      rate: this.nurse.rate,
      salary: newSalary,
      charges: newCharges,
      address: this.checkInForm.get("address")?.value,
      mealOption: mealOption.items,
      shiftType: typeOption.items,
      availabilityUpdated: false,
    };

    this.isLoading = true;

    // Payload to mark nurse as unavailable
    const nurseUpdatePayload = {
      _id: this.nurseId,
      availability: false,
    };

    // ----------------------------
    // 4) START THE PIPELINE
    // ----------------------------
    if (this.nurseId) {
      this.nurseService
        .updateNurse(nurseUpdatePayload)
        .pipe(
          // (A) If nurse update fails, stop the chain
          catchError((error) => {
            this.isLoading = false;
            this.alerts
              .open("Failed to update nurse availability", {
                label: "Error",
                appearance: "negative",
              })
              .subscribe();
            return of(null);
          }),

          // (B) Create the shift
          switchMap((nurseRes: any) => {
            if (!nurseRes) {
              return of(null);
            }
            return this.shiftService.createShift(newShift);
          }),
          catchError((error) => {
            this.isLoading = false;
            this.alerts
              .open(getHttpErrorMessage(error), {
                label: "Error",
                appearance: "negative",
              })
              .subscribe();
            return of(null);
          }),

          // (C) Once shift is created, update nurse salary record
          switchMap((shiftRes: any) => {
            this.isLoading = false;
            if (!shiftRes || !shiftRes.data) {
              return of(null);
            }

            const createdShift = shiftRes.data;
            this.alerts
              .open("Shift created successfully", {
                label: "Success",
                appearance: "success",
              })
              .subscribe();
              this.router.navigate(['nurse/nurse-profile', this.nurseId], { 
                queryParams: { refresh: 'true' } 
              });
              

            // Keep track of the newly created shift in local array
            this.shifts.push(createdShift);
            // Update local nurse object
            this.nurse.salary = newSalary;
            this.nurse.availability = false;

            // Check if we need to mark salary as 'Completed' or 'Ongoing'
            const today = new Date();
            const lastDayOfMonth = new Date(
              today.getFullYear(),
              today.getMonth() + 1,
              0
            ).getDate();
            const status =
              today.getDate() === lastDayOfMonth ? "Completed" : "Ongoing";

            // Check if a salary record exists for this month
            const currentMonth = today.getMonth() + 1;
            const currentYear = today.getFullYear();

            return this.salaryService.getSalaryByMonth(
              currentMonth,
              currentYear
            );
          }),

          // (D) Update or create the nurse’s salary record
          switchMap((salaryRes: any) => {
            const today = new Date();
            const lastDayOfMonth = new Date(
              today.getFullYear(),
              today.getMonth() + 1,
              0
            ).getDate();
            const status =
              today.getDate() === lastDayOfMonth ? "Completed" : "Ongoing";

            // Find if there's an existing record for this nurse
            const existingSalary =
              salaryRes && salaryRes.data
                ? salaryRes.data.find((s: any) => s.nurseID === this.nurseId)
                : null;

            // The newly created shift is the last in this.shifts array
            const newShiftId = this.shifts[this.shifts.length - 1]._id;

            if (existingSalary) {
              const updatePayload = {
                shifts: [...existingSalary.shifts, newShiftId],
                salary: existingSalary.salary + shiftSalary,
                status: status,
              };
              return this.salaryService.updateSalaryByNurseId(
                this.nurseId,
                updatePayload
              );
            } else {
              // Create a new salary record for the current month
              const createPayload = {
                nurseID: this.nurseId,
                shifts: [newShiftId],
                salary: shiftSalary,
                status: status,
              };
              return this.salaryService.createSalary(createPayload);
            }
          }),

          // (E) Finally, update or create the client charges record
          switchMap((salaryUpdateRes: any) => {
            if (salaryUpdateRes) {
              console.log(
                "Salary record updated/created successfully",
                salaryUpdateRes
              );
            }

            const today = new Date();
            const lastDayOfMonth = new Date(
              today.getFullYear(),
              today.getMonth() + 1,
              0
            ).getDate();
            const status =
              today.getDate() === lastDayOfMonth ? "Completed" : "Ongoing";

            // The newly created shift
            const newShiftId = this.shifts[this.shifts.length - 1]._id;

            return this.chargesService
              .getChargesByClientId(this.checkInForm.get("client")?.value._id)
              .pipe(
                catchError((error) => {
                  if (error.status === 404) {
                    // No charges record exists; proceed with creation.
                    return of({ data: null });
                  }
                  return error;
                }),
                switchMap((chargesRes: any) => {
                  const existingCharges =
                    chargesRes && chargesRes.data ? chargesRes.data : null;
                  if (existingCharges) {
                    const updatePayload = {
                      shifts: [...existingCharges.shifts, newShiftId],
                      charges: existingCharges.charges + shiftCharges,
                      status: status,
                    };
                    return this.chargesService.updateChargeByClientId(
                      this.checkInForm.get("client")?.value._id,
                      updatePayload
                    );
                  } else {
                    const createPayload = {
                      clientID: this.checkInForm.get("client")?.value._id,
                      shifts: [newShiftId],
                      charges: shiftCharges,
                      status: status,
                    };
                    return this.chargesService.createCharge(createPayload);
                  }
                })
              );
          })
        )
        .subscribe(
          (chargesUpdateRes: any) => {
            if (chargesUpdateRes) {
              console.log(
                "Client charges updated/created successfully:",
                chargesUpdateRes
              );
            }
          },
          (error) => {
            this.isLoading = false;
            console.error("Error in final subscription:", error);
          }
        );
    }

  }
}
